import { type Dispatch, type SetStateAction, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { createCase } from '../../services/cases'
import type { ClientProfile, CreateCaseInput } from '../../types/case-management'
import { logger } from '../../utils/logger'

interface CreateCaseHandlerArgs {
  fetchData: () => Promise<void>
  refetchQuota: () => Promise<void>
  setCreating: Dispatch<SetStateAction<boolean>>
  setIsNewCaseModalOpen: Dispatch<SetStateAction<boolean>>
}

export function useCreateCaseHandler({
  fetchData,
  refetchQuota,
  setCreating,
  setIsNewCaseModalOpen,
}: CreateCaseHandlerArgs): (input: CreateCaseInput) => Promise<void> {
  return useCallback(
    async (input: CreateCaseInput) => {
      setCreating(true)
      try {
        await createCase(input)
        setIsNewCaseModalOpen(false)
        void fetchData()
        void refetchQuota()
      } catch (err) {
        logger.error('OrgCasesPage', 'Failed to create case', err)
      } finally {
        setCreating(false)
      }
    },
    [fetchData, refetchQuota, setCreating, setIsNewCaseModalOpen]
  )
}

interface CreateClientHandlerArgs {
  createClient: (input: { name: string; shortName?: string }) => Promise<ClientProfile | null>
  refetchClients: () => Promise<void>
}

export function useCreateClientHandler({
  createClient,
  refetchClients,
}: CreateClientHandlerArgs): (input: { name: string; shortName?: string }) => Promise<ClientProfile | null> {
  return useCallback(
    async (input: { name: string; shortName?: string }) => {
      const result = await createClient(input)
      if (result) void refetchClients()
      return result
    },
    [createClient, refetchClients]
  )
}

interface ToggleOwnerArgs {
  setExpandedOwners: Dispatch<SetStateAction<Set<string>>>
}

export function useToggleOwner({
  setExpandedOwners,
}: ToggleOwnerArgs): (ownerId: string) => void {
  return useCallback(
    (ownerId: string) => {
      setExpandedOwners((prev) => {
        const next = new Set(prev)
        if (next.has(ownerId)) next.delete(ownerId)
        else next.add(ownerId)
        return next
      })
    },
    [setExpandedOwners]
  )
}

export function useCaseClickHandler(): (caseId: string) => void {
  const navigate = useNavigate()
  return useCallback(
    (caseId: string) => {
      void navigate(`/cases/${caseId}`)
    },
    [navigate]
  )
}
